import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, ChevronRight, FileText, Search, Truck, ClipboardList } from "lucide-react";
import api from "../lib/api";
import { Services, Industries, About as AboutSection, WhoWeServe } from "../components/LandingPage";
import { Seo, serviceSchema, breadcrumbSchema } from "../components/site/Seo";
import SEO_ROUTES from "../seo-routes.json";
import { ContactActions } from "../components/site/ContactActions";
import { ServiceAreas } from "../components/site/ServiceAreas";
import { useGovCredentials, credentialRows, CapabilityDownload } from "./CapabilityStatement";
import { GovFederalSection, FederalNote } from "./FederalPages";

export function PageHero({ eyebrow, title, sub, testid, actions = true }) {
  return (
    <section data-testid={testid} className="pt-[130px] pb-16 hero-bg">
      <div className="pg-container max-w-4xl">
        <span className="eyebrow" style={{ color: "#7EC4FF" }}>{eyebrow}</span>
        <h1 className="mt-5 text-[36px] md:text-[50px] font-extrabold leading-[1.06] text-white">{title}</h1>
        <p className="mt-5 text-[17px] text-white/80 max-w-2xl leading-relaxed">{sub}</p>
        {actions ? <ContactActions source={testid} className="mt-8" /> : (
          <div className="mt-8 flex flex-wrap gap-3">
            <Link data-testid={`${testid}-cta`} to="/request-a-quote" className="btn-primary">Submit an RFQ <ArrowRight size={18} /></Link>
          </div>
        )}
      </div>
    </section>
  );
}

export function BulletGrid({ items, testidPrefix }) {
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {items.map((it, i) => (
        <div key={it} data-testid={`${testidPrefix}-${i}`} className="lift-card bg-white border border-slate-200 rounded-xl p-5 flex items-start gap-3">
          <CheckCircle2 size={20} className="text-[var(--pg-accent)] mt-0.5 flex-shrink-0" />
          <span className="text-[15px] font-medium text-[var(--pg-navy)]">{it}</span>
        </div>
      ))}
    </div>
  );
}

export function CTABand({ label = "Submit an RFQ" }) {
  return (
    <section className="py-16 bg-[var(--pg-bg)]">
      <div className="pg-container">
        <div className="rounded-3xl bg-gradient-to-r from-[var(--pg-navy)] to-[var(--pg-navy-2)] text-white p-8 md:p-12 flex flex-wrap items-center justify-between gap-6">
          <div>
            <h2 className="font-[Poppins] font-extrabold text-[26px] md:text-[34px] leading-tight">Ready to send us your requirement?</h2>
            <p className="mt-2 text-white/85 text-[16px]">One point of contact, from RFQ to delivery. Call 281-901-9914 or email info@pgpsusa.com.</p>
          </div>
          <Link data-testid="page-cta-band-button" to="/request-a-quote" className="inline-flex items-center gap-2 bg-[var(--pg-accent)] hover:bg-white hover:text-[var(--pg-navy)] text-white font-[Poppins] font-semibold px-6 py-4 rounded-full transition-colors">
            {label} <ArrowRight size={18} />
          </Link>
        </div>
      </div>
    </section>
  );
}

export function SectionHead({ eyebrow, title, sub }) {
  return (
    <div className="max-w-3xl">
      <span className="eyebrow">{eyebrow}</span>
      <h2 className="mt-4 text-[30px] md:text-[38px] font-extrabold leading-[1.1]">{title}</h2>
      {sub && <p className="mt-4 text-[16px] text-[var(--pg-ink-soft)] leading-relaxed">{sub}</p>}
    </div>
  );
}

export function Prose({ children, testid }) {
  return <div data-testid={testid} className="mt-8 max-w-3xl space-y-4 text-[16px] text-[var(--pg-ink-soft)] leading-relaxed">{children}</div>;
}

const crumbs = (name, path) => breadcrumbSchema([{ name: "Home", path: "/" }, { name, path }]);

/* ---------------- Procurement ---------------- */
export function ProcurementPage() {
  const desc = SEO_ROUTES["/procurement"].description;
  return (
    <div data-testid="procurement-page">
      <Seo route="/procurement"
        jsonLd={[serviceSchema({ name: "Procurement & Strategic Sourcing Services", description: desc, path: "/procurement" }), crumbs("Procurement", "/procurement")]} />
      <PageHero testid="procurement-hero" eyebrow="Procurement & Strategic Sourcing"
        title="Procurement & Sourcing Services in Houston, Texas"
        sub="Send us the requirement. Patrick Global identifies suppliers, evaluates sourcing options, negotiates competitive terms, coordinates purchasing, and manages the procurement requirement through delivery." />
      <section className="py-16 md:py-24 bg-white">
        <div className="pg-container">
          <SectionHead eyebrow="Services" title="Procurement services for purchasing departments and project teams"
            sub="Whether you are a purchasing officer working a solicitation, a contractor buying for a job site, or an operations manager who needs a hard-to-find part, the process is the same: one RFQ, documented supplier options, one point of contact." />
          <div className="mt-10">
            <BulletGrid testidPrefix="procurement-service" items={[
              "Strategic sourcing", "Supplier identification & qualification", "Competitive sourcing & quote comparison",
              "Procurement management", "Vendor management", "Global sourcing",
              "RFQ management", "Procurement reporting", "Purchase & delivery coordination",
            ]} />
          </div>
          <Prose testid="procurement-prose">
            <p>
              <strong className="text-[var(--pg-navy)]">Houston procurement services with a documented process.</strong> Every requirement receives an RFQ number
              that follows it from supplier sourcing and quote comparison through purchase order, delivery, and invoice — so purchasing
              departments in Houston, Sugar Land, Katy, and across Fort Bend County have a clear record of how each item was sourced.
            </p>
            <p>
              We work across industrial and MRO supplies, safety and PPE, facility supplies, IT equipment, medical supplies, and
              equipment packages. If you already have a specification, part number, or preferred manufacturer, include it — we quote
              to your specification and flag any substitution for your approval before purchase.
            </p>
          </Prose>
        </div>
      </section>
      <FederalNote testid="procurement-federal" title="Federal & Defense Procurement"
        text="PGPS supports federal and defense-industry buyers with supplier research, part-number sourcing, RFQ response support, supplier quote comparison, availability verification, procurement coordination and delivery management."
        tags={["NSN", "NIIN", "Part Number", "CAGE", "Manufacturer", "RFQ", "Solicitation", "Lead Time", "Delivery"]} />
      <Services />
      <ServiceAreas compact />
      <CTABand label="Submit an RFQ" />
    </div>
  );
}

/* ---------------- Industrial & MRO ---------------- */
export function IndustrialMROPage() {
  const desc = SEO_ROUTES["/industrial-mro"].description;
  return (
    <div data-testid="industrial-mro-page">
      <Seo route="/industrial-mro"
        jsonLd={[serviceSchema({ name: "Industrial & MRO Supply Sourcing", description: desc, path: "/industrial-mro" }), crumbs("Industrial & MRO", "/industrial-mro")]} />
      <PageHero testid="industrial-hero" eyebrow="Industrial Procurement & MRO Supply"
        title="Industrial Supply & MRO Procurement for Texas Operations"
        sub="Patrick Global helps industrial organizations source the MRO supplies, components, and equipment that keep plants, projects, and facilities running — from a single valve to a full jobsite package." />
      <section className="py-16 md:py-24 bg-white">
        <div className="pg-container">
          <SectionHead eyebrow="What We Source" title="Industrial & MRO Categories" />
          <div className="mt-10">
            <BulletGrid testidPrefix="mro-category" items={[
              "MRO supplies", "Valves", "Pumps", "Motors", "Bearings", "Gaskets",
              "Filters", "Hoses", "Fittings", "Fasteners", "Electrical components",
              "Industrial equipment", "Safety products & PPE", "Maintenance supplies", "Facility & janitorial supplies",
            ]} />
          </div>
          <Prose testid="mro-prose">
            <p>
              <strong className="text-[var(--pg-navy)]">MRO procurement in Houston without the supplier chase.</strong> Maintenance teams along the Houston
              Ship Channel, in Fort Bend County industrial parks, and at facilities statewide send us part numbers, drawings, or a plain-language
              description. We locate qualified distributors and manufacturers, confirm availability and lead time, and return a comparison you can act on.
            </p>
            <p>
              Need a brand-specific item? Tell us the manufacturer and part number and we source to match. Open to equivalents? We will present
              options with pricing and lead time side by side and only substitute with your written approval.
            </p>
          </Prose>
        </div>
      </section>
      <FederalNote testid="mro-federal" title="Federal & Defense Supply Support"
        text="PGPS supports federal and defense-industry requirements involving industrial MRO components, replacement parts, electrical components, equipment and hard-to-source items identified by NSN, manufacturer part number or specification." />
      <section className="py-16 md:py-20 bg-[var(--pg-bg)]">
        <div className="pg-container">
          <SectionHead eyebrow="Industries Served" title="Built for industrial buyers" />
          <div className="mt-8 flex flex-wrap gap-3" data-testid="mro-industries">
            {["Energy", "Oil & Gas", "Petrochemical", "Manufacturing", "Construction", "Engineering / EPC", "Facilities", "Government & Public Works"].map((t) => (
              <span key={t} className="font-[Poppins] font-semibold text-[13.5px] bg-white border border-slate-200 text-[var(--pg-navy)] px-4 py-2 rounded-full">{t}</span>
            ))}
          </div>
        </div>
      </section>
      <CTABand label="Send Us Your Requirement" />
    </div>
  );
}

/* ---------------- Government Contracting & Capabilities ---------------- */
const GOV_SUPPLY = [
  ["Industrial & MRO supplies", "Valves, fittings, fasteners, bearings, electrical components, maintenance consumables"],
  ["Safety & PPE", "Gloves, eye and hearing protection, high-visibility apparel, respiratory protection, first-aid supplies"],
  ["Facility supplies", "Janitorial, sanitation, breakroom, and building-maintenance products for public facilities"],
  ["IT equipment", "Computers, peripherals, networking hardware, and accessories quoted to your specification"],
  ["Medical supplies", "Exam gloves, consumables, and clinic supplies through PGPS Medical Services"],
  ["Equipment", "Tools, material-handling, and site equipment packages for public works and project teams"],
  ["Logistics & delivery coordination", "Freight, shipment tracking, and delivery to the location named on your purchase order"],
  ["Strategic sourcing support", "Market research, supplier identification, and documented quote comparison for purchasing officers"],
];
const GOV_STEPS = [
  { icon: FileText, title: "Send the requirement", body: "Use the RFQ form (attach the solicitation, bid tab, or spec sheet — up to 5 files), the short inquiry form, or email info@pgpsusa.com. Include quantities, delivery location, and the response deadline." },
  { icon: Search, title: "We source and document", body: "We identify qualified suppliers, confirm availability and lead times, and return an itemized quote referencing your solicitation or requisition number." },
  { icon: Truck, title: "Award, fulfillment, delivery", body: "On award, we place the purchase order, coordinate freight to your named destination, and provide tracking and delivery confirmation." },
];
const GOV_FAQ = [
  ["What should I include in a request for quote?", "Item description or part number, quantity and unit, required specifications or approved equals, delivery address, needed-by date, and the solicitation or requisition number if one exists. Attachments are welcome."],
  ["Can you quote a specific manufacturer or part number?", "Yes. We source to the specification you provide and present any proposed equivalent separately, with pricing and lead time, for your approval before purchase."],
  ["Do you deliver outside the Houston area?", "Yes. Sourcing and quoting are handled from Houston; delivery is coordinated with suppliers and carriers to the destination on your purchase order, in Texas or in other states."],
  ["How are quotes delivered?", "As an itemized PDF referencing your RFQ number, by email. Registered customers can also review, accept, or request changes to quotes in the Client Portal."],
];

export function GovernmentPage() {
  const creds = useGovCredentials();
  const rows = credentialRows(creds);
  const past = creds?.past_performance || [];
  const desc = SEO_ROUTES["/government"].description;
  return (
    <div data-testid="government-page">
      <Seo route="/government"
        jsonLd={[serviceSchema({ name: "Government Procurement & Supply Services", description: desc, path: "/government" }), crumbs("Government Contracting & Capabilities", "/government")]} />
      <PageHero testid="government-hero" eyebrow="Government Contracting & Capabilities"
        title="Government Contracting & Capabilities"
        sub="Patrick Global supports federal, state, county, and municipal agencies, prime contractors, and subcontracting teams with responsive, documented sourcing of supplies and equipment — headquartered in Houston, Texas." />

      <section className="py-16 md:py-24 bg-white">
        <div className="pg-container">
          <SectionHead eyebrow="Products & Services" title="What we can supply to government buyers"
            sub="Commodity categories we actively source and quote. If your requirement falls outside these categories, ask — we will tell you plainly whether we can support it." />
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {GOV_SUPPLY.map(([h, b], i) => (
              <div key={h} data-testid={`gov-area-${i}`} className="lift-card bg-white border border-slate-200 rounded-xl p-5">
                <CheckCircle2 size={20} className="text-[var(--pg-accent)]" />
                <h3 className="mt-3 font-[Poppins] font-bold text-[15.5px] text-[var(--pg-navy)]">{h}</h3>
                <p className="mt-1.5 text-[13.5px] text-[var(--pg-ink-soft)] leading-relaxed">{b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <ServiceAreas compact />

      <section className="py-16 md:py-24 bg-white" data-testid="gov-how-to-quote">
        <div className="pg-container">
          <SectionHead eyebrow="For Purchasing Officers" title="How to request a quote"
            sub="Three steps, one point of contact. We respond to new requirements within one business day." />
          <div className="mt-10 grid md:grid-cols-3 gap-5">
            {GOV_STEPS.map((s, i) => (
              <div key={s.title} data-testid={`gov-step-${i}`} className="bg-[var(--pg-bg)] border border-slate-200 rounded-2xl p-7">
                <div className="flex items-center justify-between">
                  <div className="w-11 h-11 rounded-lg bg-[var(--pg-navy)] text-white flex items-center justify-center"><s.icon size={20} /></div>
                  <span className="font-[Poppins] font-extrabold text-[28px] text-slate-300">0{i + 1}</span>
                </div>
                <h3 className="mt-5 font-[Poppins] font-semibold text-[18px] text-[var(--pg-navy)]">{s.title}</h3>
                <p className="mt-2 text-[14px] text-[var(--pg-ink-soft)] leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
          <ContactActions source="gov_how_to" dark={false} className="mt-10" />
        </div>
      </section>

      <section className="py-16 bg-[var(--pg-bg)]" data-testid="gov-credentials-section">
        <div className="pg-container max-w-3xl">
          <SectionHead eyebrow="Business Identifiers" title="Registrations, identifiers & classifications"
            sub="Only verified identifiers are published here. Certifications, contract vehicles, and past performance are listed once documented." />
          {rows.length > 0 ? (
            <dl className="mt-8 bg-white border border-slate-200 rounded-2xl divide-y divide-slate-100">
              {rows.map(([label, value]) => (
                <div key={label} className="flex flex-wrap gap-2 justify-between px-6 py-4" data-testid={`gov-cred-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <dt className="font-[Poppins] font-semibold text-[14px] text-[var(--pg-ink-soft)]">{label}</dt>
                  <dd className="text-[14.5px] font-medium text-[var(--pg-navy)] text-right max-w-[70%]">{value}</dd>
                </div>
              ))}
            </dl>
          ) : creds && (
            <p className="mt-8 text-[14.5px] text-[var(--pg-ink-soft)]" data-testid="gov-cred-pending">Business identifiers are being verified and will be published here. Contact us for our capability statement in the meantime.</p>
          )}
          {past.length > 0 && (
            <div className="mt-6 bg-white border border-slate-200 rounded-2xl p-6" data-testid="gov-past-performance">
              <h3 className="font-[Poppins] font-bold text-[15px] text-[var(--pg-navy)]">Past Performance</h3>
              <ul className="mt-3 list-disc pl-5 space-y-1 text-[14.5px] text-[var(--pg-ink-soft)]">{past.map((p) => <li key={p}>{p}</li>)}</ul>
            </div>
          )}
          <div className="mt-8 flex flex-wrap gap-3 items-center">
            <CapabilityDownload />
            <Link to="/capability-statement" data-testid="capability-statement-web-link" className="inline-flex items-center gap-2 font-[Poppins] font-semibold text-[13.5px] text-[var(--pg-navy)] hover:text-[var(--pg-accent)] px-2">
              <ClipboardList size={16} /> View web version <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      <GovFederalSection />

      <section className="py-16 md:py-24 bg-white" data-testid="gov-faq">
        <div className="pg-container max-w-3xl">
          <SectionHead eyebrow="FAQ" title="Questions purchasing officers ask"
            sub={<>New to sending requirements? Read our guide: <Link to="/guides/how-to-write-an-rfq" data-testid="gov-guide-link" className="font-semibold text-[var(--pg-navy)] hover:text-[var(--pg-accent)]">How to write an RFQ that gets accurate quotes →</Link></>} />
          <div className="mt-8 divide-y divide-slate-200 border-y border-slate-200">
            {GOV_FAQ.map(([q, a], i) => (
              <details key={q} data-testid={`gov-faq-${i}`} className="group py-4">
                <summary className="cursor-pointer list-none flex items-center justify-between gap-4 font-[Poppins] font-semibold text-[15.5px] text-[var(--pg-navy)]">
                  {q} <ChevronRight size={18} className="text-[var(--pg-accent)] transition-transform group-open:rotate-90 flex-shrink-0" />
                </summary>
                <p className="mt-3 text-[14.5px] text-[var(--pg-ink-soft)] leading-relaxed">{a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>
      <CTABand />
    </div>
  );
}

/* ---------------- Logistics ---------------- */
const LOGISTICS_FLOW = ["Supplier", "Procurement", "Documentation", "Freight", "Warehouse", "Delivery"];

export function LogisticsPage() {
  const desc = SEO_ROUTES["/logistics"].description;
  return (
    <div data-testid="logistics-page">
      <Seo route="/logistics"
        jsonLd={[serviceSchema({ name: "Logistics & Delivery Coordination", description: desc, path: "/logistics" }), crumbs("Logistics", "/logistics")]} />
      <PageHero testid="logistics-hero" eyebrow="Logistics & Delivery"
        title="Procurement Doesn't End at the Purchase Order"
        sub="Patrick Global coordinates domestic freight, international logistics, warehousing, distribution, last-mile delivery, and shipment coordination — so your order arrives intact, on time, and documented." />
      <section className="py-16 md:py-24 bg-white">
        <div className="pg-container">
          <SectionHead eyebrow="Capabilities" title="Logistics coordination, end to end" />
          <div className="mt-10">
            <BulletGrid testidPrefix="logistics-capability" items={[
              "Domestic freight", "International logistics", "Warehousing",
              "Distribution", "Last-mile delivery", "Shipment coordination",
            ]} />
          </div>
          <div className="mt-14 overflow-x-auto">
            <div className="flex items-center gap-2 min-w-[720px]" data-testid="logistics-flow">
              {LOGISTICS_FLOW.map((s, i) => (
                <div key={s} className="flex items-center gap-2 flex-1">
                  <div className="flex-1 bg-[var(--pg-bg)] border border-slate-200 rounded-xl py-4 px-3 text-center font-[Poppins] font-semibold text-[13.5px] text-[var(--pg-navy)]">{s}</div>
                  {i < LOGISTICS_FLOW.length - 1 && <ChevronRight size={18} className="text-[var(--pg-accent)] flex-shrink-0" />}
                </div>
              ))}
            </div>
          </div>
          <Prose testid="logistics-prose">
            <p>
              Delivery is coordinated from our Houston headquarters to the destination you specify — a Fort Bend County facility, a Texas job site,
              or an agency location in another state. Customers receive carrier and tracking details as shipments move, and delivery confirmation when they arrive.
            </p>
          </Prose>
        </div>
      </section>
      <FederalNote testid="logistics-federal" title="Federal Delivery Coordination"
        text="PGPS coordinates supplier shipments and freight to customer-designated destinations, including government and contractor facilities, subject to applicable purchase order, solicitation and shipping requirements."
        items={["Shipment tracking", "Delivery confirmation", "Packing documentation", "Supplier documentation", "Commercial invoices", "Other documentation required by the purchase order"]} />
      <CTABand />
    </div>
  );
}

/* ---------------- Procurement-as-a-Service ---------------- */
export function PaaSPage() {
  const [plans, setPlans] = useState([]);
  useEffect(() => {
    api.get("/settings/public").then(({ data }) => setPlans(data?.pricing_plans || [])).catch(() => {});
  }, []);
  const desc = SEO_ROUTES["/procurement-as-a-service"].description;
  return (
    <div data-testid="paas-page">
      <Seo route="/procurement-as-a-service"
        jsonLd={[serviceSchema({ name: "Procurement-as-a-Service", description: desc, path: "/procurement-as-a-service" }), crumbs("Procurement-as-a-Service", "/procurement-as-a-service")]} />
      <PageHero testid="paas-hero" eyebrow="Procurement-as-a-Service"
        title="Your Outsourced Procurement Department"
        sub="Get ongoing procurement support without building a larger internal purchasing department. Patrick Global becomes your sourcing, negotiation, and purchasing arm." />
      <section className="py-16 md:py-24 bg-white">
        <div className="pg-container">
          <SectionHead eyebrow="What's Included" title="Everything a purchasing department does" />
          <div className="mt-10">
            <BulletGrid testidPrefix="paas-service" items={[
              "Supplier research", "RFQ management", "Price comparison",
              "Supplier negotiation", "Purchase coordination", "Vendor management",
              "Procurement reporting", "Sourcing strategy", "Logistics coordination",
            ]} />
          </div>
        </div>
      </section>
      {plans.length > 0 && (
        <section className="py-16 md:py-20 bg-[var(--pg-bg)]" data-testid="paas-pricing-section">
          <div className="pg-container">
            <SectionHead eyebrow="Plans" title="Simple monthly engagement plans" />
            <div className="mt-10 grid md:grid-cols-3 gap-5">
              {plans.map((p, i) => (
                <div key={p.name} data-testid={`paas-plan-${i}`} className={`rounded-2xl p-7 flex flex-col lift-card ${i === 1 ? "bg-gradient-to-b from-[var(--pg-navy)] to-[var(--pg-navy-2)] text-white" : "bg-white border border-slate-200"}`}>
                  <h3 className={`font-[Poppins] font-bold text-[20px] ${i === 1 ? "text-white" : "text-[var(--pg-navy)]"}`}>{p.name}</h3>
                  <div className={`mt-3 font-[Poppins] font-extrabold text-[30px] ${i === 1 ? "text-[var(--pg-accent)]" : "text-[var(--pg-navy)]"}`}>{p.price}</div>
                  <ul className="mt-5 space-y-2.5 flex-1">
                    {(p.features || []).map((f) => (
                      <li key={f} className={`flex items-start gap-2.5 text-[14px] ${i === 1 ? "text-white/85" : "text-[var(--pg-ink-soft)]"}`}>
                        <CheckCircle2 size={17} className="text-[var(--pg-accent)] mt-0.5 flex-shrink-0" />{f}
                      </li>
                    ))}
                  </ul>
                  <Link to="/request-a-quote" data-testid={`paas-plan-${i}-cta`}
                    className={`mt-7 inline-flex items-center justify-center gap-2 font-[Poppins] font-semibold px-5 py-3.5 rounded-full transition-colors ${i === 1 ? "bg-[var(--pg-accent)] text-white hover:bg-white hover:text-[var(--pg-navy)]" : "bg-[var(--pg-navy)] text-white hover:bg-[var(--pg-accent)]"}`}>
                    Schedule a Consultation <ArrowRight size={16} />
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
      <CTABand label="Schedule a Procurement Consultation" />
    </div>
  );
}

/* ---------------- Industries ---------------- */
export function IndustriesPage() {
  return (
    <div data-testid="industries-page">
      <Seo route="/industries" jsonLd={crumbs("Industries", "/industries")} />
      <PageHero testid="industries-hero" eyebrow="Industries"
        title="Sector Expertise Across the Supply Chain"
        sub="We provide tailored procurement and supply chain solutions for energy, oil & gas, construction, manufacturing, government, healthcare, technology, education, facilities, and growing businesses." />
      <Industries />
      <section className="py-4 md:py-8 bg-white">
        <div className="pg-container">
          <BulletGrid testidPrefix="industries-extra" items={["Education", "Facilities", "Oil & Gas", "Construction"]} />
        </div>
      </section>
      <WhoWeServe />
      <CTABand />
    </div>
  );
}

/* ---------------- About ---------------- */
export function AboutPage() {
  return (
    <div data-testid="about-page">
      <Seo route="/about" jsonLd={crumbs("About", "/about")} />
      <PageHero testid="about-hero" eyebrow="About Patrick Global"
        title="Connecting Organizations With Procurement Solutions"
        sub="Our mission: to make procurement simpler, more efficient, and more accessible for organizations that need reliable sourcing and supply-chain support." />
      <AboutSection />
      <section className="py-16 bg-[var(--pg-bg)]">
        <div className="pg-container grid md:grid-cols-2 gap-6">
          <div className="bg-white border border-slate-200 rounded-2xl p-8" data-testid="about-mission-card">
            <span className="eyebrow">Mission</span>
            <p className="mt-4 text-[16.5px] text-[var(--pg-navy)] font-medium leading-relaxed">
              To make procurement simpler, more efficient, and more accessible for organizations
              that need reliable sourcing and supply-chain support.
            </p>
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl p-8" data-testid="about-vision-card">
            <span className="eyebrow">Vision</span>
            <p className="mt-4 text-[16.5px] text-[var(--pg-navy)] font-medium leading-relaxed">
              To build a technology-enabled global procurement company connecting organizations
              with qualified suppliers and efficient supply-chain solutions.
            </p>
          </div>
        </div>
      </section>
      <CTABand />
    </div>
  );
}
